import { MarginIcon } from '../../../../../svg/margin-icon';
import { PaddingIcon } from '../../../../../svg/padding-icon';

import { SliderWithBadge } from '../../../../../primitives/slider';
import { LabelWithoutRef } from '../../../../label/label-without-ref';

import { cn } from '../../../../../utils/cn';

const paddingControls = [
  { key: 'top', label: 'Padding superior', Icon: <PaddingIcon className="mb-1.25" /> },
  {
    key: 'bottom',
    label: 'Padding inferior',
    Icon: <PaddingIcon className="mb-1.25 rotate-180" />,
  },
  { key: 'left', label: 'Padding izquierdo', Icon: <PaddingIcon className="mb-1.25 rotate-270" /> },
  { key: 'right', label: 'Padding derecho', Icon: <PaddingIcon className="mb-1.25 rotate-90" /> },
] as const;

const marginControls = [
  { key: 'top', label: 'Margen superior', Icon: <MarginIcon className="mb-1.25" /> },
  {
    key: 'bottom',
    label: 'Margen inferior',
    Icon: <MarginIcon className="mb-1.25 rotate-180" />,
  },
  { key: 'left', label: 'Margen izquierdo', Icon: <MarginIcon className="mb-1.25 rotate-270" /> },
  { key: 'right', label: 'Margen derecho', Icon: <MarginIcon className="mb-1.25 rotate-90" /> },
] as const;

export type SpacingValues = Record<'top' | 'bottom' | 'left' | 'right', number>;

type SpacingProps = {
  className?: string;
  margin?: SpacingValues | null;
  onChangeMargin: (value: SpacingValues) => void;
  isPaddingVisible?: boolean;
  paddingBottom: number;
  paddingLeft: number;
  paddingRight: number;
  paddingTop: number;
  onChangePaddingBottom?: (value: number) => void;
  onChangePaddingLeft?: (value: number) => void;
  onChangePaddingRight?: (value: number) => void;
  onChangePaddingTop?: (value: number) => void;
};

export function Spacing({
  className,
  margin,
  onChangeMargin,
  paddingTop,
  paddingBottom,
  paddingLeft,
  paddingRight,
  isPaddingVisible,
  onChangePaddingBottom,
  onChangePaddingLeft,
  onChangePaddingRight,
  onChangePaddingTop,
}: SpacingProps) {
  if (!isPaddingVisible) {
    return null;
  }

  const paddingValues = {
    top: paddingTop,
    right: paddingRight,
    bottom: paddingBottom,
    left: paddingLeft,
  };
  const paddingChangeHandlers = {
    top: onChangePaddingTop,
    right: onChangePaddingRight,
    bottom: onChangePaddingBottom,
    left: onChangePaddingLeft,
  };

  return (
    <div className={cn('space-y-2', className)}>
      <p className="text-xs font-medium">Espaciado</p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <div className="col-span-2">
          <LabelWithoutRef className="text-xs font-medium text-muted-foreground">
            Margen interno
          </LabelWithoutRef>
        </div>
        {paddingControls.map(({ key, label, Icon }) => (
          <div
            key={key}
            className={cn('grid grid-cols-[1rem_1fr] items-end gap-2', {
              'pb-1': key === 'top' || key === 'bottom',
            })}
          >
            {Icon}
            <SliderWithBadge
              aria-label={label}
              max={60}
              min={0}
              step={4}
              showStepMarks
              variant="accent"
              value={paddingValues[key]}
              onValueChange={paddingChangeHandlers[key]}
            />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <div className="col-span-2">
          <LabelWithoutRef className="text-xs font-medium text-muted-foreground">
            Margen externo
          </LabelWithoutRef>
        </div>
        {marginControls.map(({ key, label, Icon }) => (
          <div
            key={key}
            className={cn('grid grid-cols-[1rem_1fr] items-end gap-2', {
              'pb-1': key === 'top' || key === 'bottom',
            })}
          >
            {Icon}
            <SliderWithBadge
              aria-label={label}
              max={60}
              min={0}
              step={4}
              showStepMarks
              variant="accent"
              value={margin?.[key] ?? 0}
              onValueChange={(value) =>
                onChangeMargin({
                  top: 0,
                  bottom: 0,
                  left: 0,
                  right: 0,
                  ...margin,
                  [key]: value,
                })
              }
            />
          </div>
        ))}
      </div>
    </div>
  );
}
